import { useEffect, useMemo, useState } from 'react';
import { goBack, resetNav, type PoolFocus } from '../../app/navigation';
import type { V2Evidence } from '../../domain/evidence';
import { displayAddress, isInspectionLocked, type V2Area } from '../../domain/inspection';
import type { V2Finding } from '../../domain/finding';
import { useT } from '../../i18n/i18n';
import { areaKindLabel, areaDisplayName, floorLabelFor, itemTitleFor } from '../../i18n/labels';
import { linkExistingEvidenceAction } from '../../state/captureFlow';
import { useStore } from '../../state/createStore';
import { evidenceStore } from '../../state/evidenceStore';
import { findingStore, findingsOfArea, unlinkEvidenceFromFindingAction } from '../../state/findingStore';
import { areasOf, inspectionStore, itemsOf, unlinkEvidenceFromItemAction } from '../../state/inspectionStore';
import { buildPhotoPool, entriesForFocus, entryIsLinked, entryIsUnclassified, filterPool, pendingLinks, poolAreaIndex, poolCounts, POOL_FILTER_ORDER, type PoolEntry, type PoolFilter, type PoolLink } from '../../state/photoPool';
import { poolAiLiveOf, poolAiResultOf, poolAiStore } from '../../state/poolAiStore';
import { AREA_KIND_ICONS } from '../../ui/areaIcons';
import { useStickyFooterClearance } from '../../ui/stickyFooter';
import { ActionSheet, Banner, Button, ConfirmSheet, EmptyState, FilterChips, Icon, InlineMessage, SheetShell, StatusPill, TopBar, showToast } from '../../ui/components';
import { Lightbox } from '../evidence/Lightbox';
import { PhotoNoteSheet } from '../evidence/PhotoNoteSheet';
import { Thumbnail } from '../evidence/PhotoStrip';
import { PoolAiCard } from './PoolAiCard';
import { DeletePhotosConfirm, ImportProgressBar, usePoolImport } from './poolImport';

// The Photo Pool (Round 1 §9 + Round 2 §S / §T): area-first. The index lists every area with its
// photo count, then 未分类 and 查看全部; opening one shows that group as a grid with All / Unlinked /
// Linked, multi-select, link to an item or an existing finding of any area (one photo → many
// targets), remove a link, add more photos, and a note on every card. Linking never moves or copies.
// Owner device test 2026-10-05: the import shows its progress bar and percentage; every photo can be
// deleted (one, or the selection); 「AI 分析照片池」 sits at the top of the index (its progress and
// outcome too) and in the grid's footer, behind the reminder that notes make the analysis better.
type LinkStep = { step: 'area' } | { step: 'target'; area: V2Area };

export function PhotoPoolScreen(props: { inspectionId: string; focus?: PoolFocus }) {
  const { t, tp, locale } = useT();
  const footerRef = useStickyFooterClearance();
  const inspectionState = useStore(inspectionStore, (s) => s);
  const evidenceState = useStore(evidenceStore, (s) => s);
  const findingState = useStore(findingStore, (s) => s);
  const inspection = inspectionState.inspections.find((i) => i.id === props.inspectionId) ?? null;
  const entries = useMemo(() => buildPhotoPool(props.inspectionId, evidenceState, inspectionState, findingState), [props.inspectionId, evidenceState, inspectionState, findingState]);
  const areas = useMemo(() => areasOf(inspectionState, props.inspectionId).filter((a) => a.enabled), [inspectionState, props.inspectionId]);
  const index = useMemo(() => poolAreaIndex(entries, areas), [entries, areas]);
  const [focus, setFocus] = useState<PoolFocus | null>(props.focus ?? null);
  useEffect(() => setFocus(props.focus ?? null), [props.focus]);
  const focused = useMemo(() => (focus ? entriesForFocus(entries, focus) : []), [entries, focus]);
  const counts = useMemo(() => poolCounts(focused), [focused]);
  const [filter, setFilter] = useState<PoolFilter>('all');
  const visible = useMemo(() => filterPool(focused, filter), [focused, filter]);
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [linkStep, setLinkStep] = useState<LinkStep | null>(null);
  const [unlinkTarget, setUnlinkTarget] = useState<{ evidenceId: string; link: PoolLink } | null>(null);
  const [noteFor, setNoteFor] = useState<V2Evidence | null>(null);
  const [lightbox, setLightbox] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [deleteIds, setDeleteIds] = useState<string[] | null>(null);
  const importer = usePoolImport(props.inspectionId);
  const poolAiResult = useStore(poolAiStore, (s) => poolAiResultOf(s, props.inspectionId));
  const poolAiLive = useStore(poolAiStore, (s) => poolAiLiveOf(s, props.inspectionId));

  if (!inspection) {
    return (
      <div className="screen">
        <TopBar title={t('pool.title')} onBack={() => resetNav({ screen: 'inspectionList' })} />
        <EmptyState title={t('inspection.notFoundTitle')} body={t('inspection.notFoundBody')} />
      </div>
    );
  }

  const locked = isInspectionLocked(inspection);
  const areaById = new Map(areas.map((a) => [a.id, a] as const));
  const itemById = new Map(inspectionState.items.map((i) => [i.id, i] as const));
  const address = displayAddress(inspection, t('inspection.noAddress'));

  function toggle(id: string) {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  function tapPhoto(entry: PoolEntry, i: number) {
    if (selecting) toggle(entry.evidence.id);
    else setLightbox(i);
  }

  function leaveFocus() {
    setSelecting(false);
    setSelected([]);
    setFilter('all');
    setFocus(null);
  }

  async function handleImport() {
    const imported = await importer.run();
    if (imported.length > 0 && !focus) setFocus({ kind: 'unclassified' });
  }

  async function linkTo(target: { itemId: string | null; findingId: string | null; label: string }) {
    setBusy(true);
    try {
      for (const id of selected) await linkExistingEvidenceAction(id, { inspectionId: props.inspectionId, itemId: target.itemId, findingId: target.findingId });
      showToast(t('pool.linkedToast', { n: selected.length, target: target.label }));
      setSelected([]);
      setSelecting(false);
      setLinkStep(null);
    } finally {
      setBusy(false);
    }
  }

  async function confirmUnlink() {
    if (!unlinkTarget) return;
    const { evidenceId, link } = unlinkTarget;
    setUnlinkTarget(null);
    if (link.kind === 'item') await unlinkEvidenceFromItemAction(link.id, evidenceId);
    else await unlinkEvidenceFromFindingAction(link.id, evidenceId);
  }

  function linkLabel(link: PoolLink): string {
    const area = areaById.get(link.areaId);
    const item = link.kind === 'item' ? (itemById.get(link.id) ?? null) : null;
    const title = item ? itemTitleFor(locale, item) : link.title.length > 0 ? link.title : t('finding.otherTitle');
    return `${area ? areaDisplayName(locale, area) + ' · ' : ''}${title}`;
  }

  function focusTitle(f: PoolFocus): string {
    switch (f.kind) {
      case 'all':
        return t('pool.allPhotos');
      case 'unclassified':
        return t('pool.unclassified');
      case 'area': {
        const area = areaById.get(f.areaId);
        return area ? areaDisplayName(locale, area) : t('pool.title');
      }
    }
  }

  const importButton = !locked ? (
    <Button variant="secondary" icon="upload" onClick={() => void handleImport()} disabled={busy || importer.busy} testId="pool-import">
      {importer.busy ? t('photo.importing') : index.total === 0 ? t('import.pick') : t('import.pickMore')}
    </Button>
  ) : null;
  const importMessage = importer.message && !importer.busy ? <InlineMessage kind={importer.message.kind} text={importer.message.text} /> : null;
  const importBar = importer.progress ? (
    <div ref={footerRef} className="sticky-actions" data-testid="pool-import-bar">
      <ImportProgressBar progress={importer.progress} />
    </div>
  ) : null;
  const deleteConfirm = deleteIds ? (
    <DeletePhotosConfirm
      ids={deleteIds}
      linked={deleteIds.filter((id) => entries.some((e) => e.evidence.id === id && entryIsLinked(e))).length}
      onClose={() => setDeleteIds(null)}
      onDeleted={() => {
        setSelected([]);
        setSelecting(false);
      }}
    />
  ) : null;

  // ---------- index (area-first) ----------
  if (!focus) {
    return (
      <div className="screen" data-testid="photo-pool-screen" data-view="index">
        <TopBar status={null} title={t('pool.title')} subtitle={`${address} · ${tp('common.photosCount', index.total)}`} onBack={goBack} />
        <div className="screen-body">
          {locked ? (
            <div className="banner-slot">
              <Banner tone="info" icon="lock" title={t('report.lockedTitle')} testId="pool-locked" />
            </div>
          ) : null}
          {importMessage}
          {index.total === 0 ? (
            <EmptyState icon="perm_media" title={t('pool.emptyTitle')} body={t('pool.emptyBody')} action={importButton ?? undefined} />
          ) : (
            <>
              {!locked || poolAiResult ? <PoolAiCard inspection={inspection} onAddNotes={() => setFocus({ kind: 'unclassified' })} testId="pool-ai-card" /> : null}
              <p className="card-note pool-index-hint">{t('pool.indexHint')}</p>
              <div className="pool-index" data-testid="pool-index">
                <button type="button" className="pool-index-row pool-index-row-all" onClick={() => setFocus({ kind: 'all' })} data-testid="pool-index-all" data-count={index.total}>
                  <span className="icon-tile">
                    <Icon name="perm_media" />
                  </span>
                  <span className="pool-index-main">
                    <span className="pool-index-title">{t('pool.allPhotos')}</span>
                  </span>
                  <span className="pool-index-count">{tp('common.photosCount', index.total)}</span>
                  <Icon name="chevron_right" className="pool-index-chevron" />
                </button>
                {index.rows.map((row) => (
                  <button
                    type="button"
                    key={row.area.id}
                    className={`pool-index-row ${row.count === 0 ? 'pool-index-row-empty' : ''}`}
                    onClick={() => setFocus({ kind: 'area', areaId: row.area.id })}
                    data-testid="pool-index-area"
                    data-area-id={row.area.id}
                    data-count={row.count}
                  >
                    <span className="icon-tile">
                      <Icon name={AREA_KIND_ICONS[row.area.kind]} />
                    </span>
                    <span className="pool-index-main">
                      <span className="pool-index-title">{areaDisplayName(locale, row.area)}</span>
                      <span className="pool-index-sub">{[row.area.floor !== null ? floorLabelFor(t, row.area.floor) : null, areaKindLabel(t, row.area.kind)].filter((x): x is string => x !== null).join(' • ')}</span>
                    </span>
                    <span className="pool-index-count">{tp('common.photosCount', row.count)}</span>
                    <Icon name="chevron_right" className="pool-index-chevron" />
                  </button>
                ))}
                <button type="button" className={`pool-index-row ${index.unclassified === 0 ? 'pool-index-row-empty' : ''}`} onClick={() => setFocus({ kind: 'unclassified' })} data-testid="pool-index-unclassified" data-count={index.unclassified}>
                  <span className="icon-tile icon-tile-soft">
                    <Icon name="link_off" />
                  </span>
                  <span className="pool-index-main">
                    <span className="pool-index-title">{t('pool.unclassified')}</span>
                    <span className="pool-index-sub">{t('pool.unclassifiedHint')}</span>
                  </span>
                  <span className="pool-index-count">{tp('common.photosCount', index.unclassified)}</span>
                  <Icon name="chevron_right" className="pool-index-chevron" />
                </button>
              </div>
              {importButton ? <div className="screen-cta">{importButton}</div> : null}
            </>
          )}
        </div>
        {importBar}
      </div>
    );
  }

  // ---------- one group as a grid ----------
  const lightboxEvidence = visible.map((e) => e.evidence);
  const fromRoute = props.focus !== undefined && focus === props.focus;
  // 「AI 分析照片池」 works on the unclassified photos: their notes are what the reminder asks for.
  const unclassified = entries.filter(entryIsUnclassified);
  const noteless = focus.kind === 'area' ? 0 : focused.filter((e) => entryIsUnclassified(e) && e.evidence.caption.trim().length === 0).length;
  const aiFooter = !locked && focus.kind !== 'area' && (unclassified.length > 0 || poolAiResult !== null || poolAiLive !== null);

  return (
    <div className="screen" data-testid="photo-pool-screen" data-view="grid" data-focus={focus.kind}>
      <TopBar
        status={null}
        title={focusTitle(focus)}
        subtitle={`${t('pool.title')} · ${tp('common.photosCount', focused.length)}`}
        onBack={fromRoute ? goBack : leaveFocus}
        right={
          focused.length > 0 && !locked ? (
            <Button
              variant="text"
              size="small"
              block={false}
              onClick={() => {
                setSelecting((v) => !v);
                setSelected([]);
              }}
              testId="pool-select-toggle"
            >
              {selecting ? t('shell.cancel') : t('pool.select')}
            </Button>
          ) : undefined
        }
      />
      <div className="screen-body">
        {locked ? (
          <div className="banner-slot">
            <Banner tone="info" icon="lock" title={t('report.lockedTitle')} testId="pool-locked" />
          </div>
        ) : null}
        <div className="pool-toolbar">
          <Button variant="text" size="small" block={false} icon="grid_view" onClick={leaveFocus} testId="pool-by-area">
            {t('pool.byArea')}
          </Button>
        </div>
        <div className="pool-filters">
          <FilterChips
            options={POOL_FILTER_ORDER.map((key) => ({ key, label: t(key === 'all' ? 'pool.filterAll' : key === 'unlinked' ? 'pool.filterUnlinked' : 'pool.filterLinked'), count: counts[key] }))}
            value={filter}
            onChange={setFilter}
          />
        </div>
        {importMessage}
        {noteless > 0 && !locked ? (
          <p className="card-note pool-notes-hint" data-testid="pool-notes-hint">
            <Icon name="mode_comment" /> {tp('poolAi.notesHint', noteless)}
          </p>
        ) : null}
        {focused.length === 0 ? (
          <EmptyState icon="perm_media" title={t('pool.groupEmptyTitle')} body={focus.kind === 'unclassified' ? t('pool.unclassifiedEmptyBody') : t('pool.groupEmptyBody')} action={importButton ?? undefined} />
        ) : (
          <div className="pool-body">
            {visible.length === 0 ? <p className="card-note">{t('area.noMatch')}</p> : null}
            <div className="photo-row pool-grid" data-testid="pool-grid">
              {visible.map((entry, i) => (
                <div className="pool-card" key={entry.evidence.id} data-testid="pool-card" data-linked={entryIsLinked(entry) ? 'true' : 'false'} data-pending={pendingLinks(entry).length}>
                  {/* 2026-10-05: the card's own note line is the note entry here (it used to sit dead beside a
                      second 添加备注 button — the reminder before the AI analysis sends the inspector here). */}
                  <Thumbnail
                    evidence={entry.evidence}
                    selected={selecting && selected.includes(entry.evidence.id)}
                    onClick={() => tapPhoto(entry, i)}
                    onEditNote={!locked ? setNoteFor : undefined}
                    onRemove={!locked && !selecting && !importer.busy ? () => setDeleteIds([entry.evidence.id]) : undefined}
                  />
                  <div className="pool-links">
                    {entry.captureArea ? (
                      <span className="badge badge-muted" data-testid="pool-capture-area">
                        {areaDisplayName(locale, entry.captureArea)}
                      </span>
                    ) : null}
                    {/* 2026-10-27: a link the AI placed is a link; the chip says so, and unlinking it works like any other. */}
                    {pendingLinks(entry).map((link) => (
                      <span className="badge badge-muted pool-pending-chip" key={`pending-${link.kind}-${link.id}`} data-testid="pool-pending-chip">
                        {t('pool.pendingLink', { target: linkLabel(link) })}
                      </span>
                    ))}
                    {!entryIsLinked(entry) ? (
                      <span className="pool-unlinked" data-testid="pool-unlinked-tag">
                        {t('pool.filterUnlinked')}
                      </span>
                    ) : (
                      entry.links.map((link) => (
                        <button
                          type="button"
                          key={`${link.kind}-${link.id}`}
                          className="pool-link-chip"
                          onClick={() => !locked && setUnlinkTarget({ evidenceId: entry.evidence.id, link })}
                          disabled={locked}
                          data-testid="pool-link-chip"
                          aria-label={t('pool.removeLink')}
                        >
                          <Icon name={link.kind === 'item' ? 'checklist' : 'warning'} />
                          <span>{linkLabel(link)}</span>
                          {!locked ? <Icon name="link_off" className="pool-link-remove" /> : null}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>
            {importButton ? <div className="screen-cta">{importButton}</div> : null}
          </div>
        )}
      </div>

      {importBar ??
        (selecting ? (
          <div ref={footerRef} className="sticky-actions" data-testid="pool-select-bar">
            <Button icon="link" onClick={() => setLinkStep({ step: 'area' })} disabled={selected.length === 0 || busy} testId="pool-link">
              {t('pool.linkSelected', { n: selected.length })}
            </Button>
            <Button variant="secondary" icon="delete" onClick={() => setDeleteIds([...selected])} disabled={selected.length === 0 || busy} testId="pool-delete-selected">
              {t('pool.deleteSelected', { n: selected.length })}
            </Button>
          </div>
        ) : aiFooter ? (
          <div ref={footerRef} className="sticky-actions" data-testid="pool-ai-bar">
            <PoolAiCard compact inspection={inspection} onAddNotes={() => setFocus({ kind: 'unclassified' })} onStarted={leaveFocus} onShowResult={leaveFocus} />
          </div>
        ) : null)}

      {deleteConfirm}

      {linkStep?.step === 'area' ? (
        <ActionSheet
          title={t('pool.linkToArea')}
          subtitle={tp('common.photosCount', selected.length)}
          onClose={() => setLinkStep(null)}
          testId="pool-link-area-sheet"
          items={areas.map((area) => ({
            key: area.id,
            label: areaDisplayName(locale, area),
            detail: areaKindLabel(t, area.kind),
            icon: AREA_KIND_ICONS[area.kind],
            onSelect: () => setLinkStep({ step: 'target', area }),
            testId: `pool-link-area-${area.id}`
          }))}
        />
      ) : null}

      {linkStep?.step === 'target' ? (
        <LinkTargetSheet
          area={linkStep.area}
          items={itemsOf(inspectionState, linkStep.area.id)}
          findings={findingsOfArea(findingState, linkStep.area.id).filter((f) => f.status !== 'rejected')}
          busy={busy}
          onBack={() => setLinkStep({ step: 'area' })}
          onClose={() => setLinkStep(null)}
          onPick={(target) => void linkTo(target)}
        />
      ) : null}

      {unlinkTarget ? (
        <ConfirmSheet title={t('pool.removeLinkTitle')} body={t('pool.removeLinkBody', { target: linkLabel(unlinkTarget.link) })} confirmLabel={t('pool.removeLink')} danger onConfirm={() => void confirmUnlink()} onCancel={() => setUnlinkTarget(null)} />
      ) : null}

      {noteFor ? <PhotoNoteSheet evidence={evidenceState.evidence.find((e) => e.id === noteFor.id) ?? noteFor} onClose={() => setNoteFor(null)} /> : null}

      {lightbox !== null && lightboxEvidence[lightbox] ? (
        <Lightbox evidence={lightboxEvidence} index={lightbox} onIndexChange={setLightbox} onClose={() => setLightbox(null)} context={{ itemId: null, findingId: null }} />
      ) : null}
    </div>
  );
}

function LinkTargetSheet(props: {
  area: V2Area;
  items: ReturnType<typeof itemsOf>;
  findings: V2Finding[];
  busy: boolean;
  onBack: () => void;
  onClose: () => void;
  onPick: (target: { itemId: string | null; findingId: string | null; label: string }) => void;
}) {
  const { t, locale } = useT();
  return (
    <SheetShell
      title={areaDisplayName(locale, props.area)}
      subtitle={t('pool.linkToTarget')}
      onClose={props.onClose}
      testId="pool-link-target-sheet"
      footer={
        <Button variant="text" onClick={props.onBack} disabled={props.busy} testId="pool-link-back">
          {t('shell.back')}
        </Button>
      }
    >
      <div className="option-list" role="listbox">
        {props.items.map((item) => (
          <button key={item.id} type="button" role="option" aria-selected={false} className="option-row" onClick={() => props.onPick({ itemId: item.id, findingId: null, label: itemTitleFor(locale, item) })} disabled={props.busy} data-testid="pool-link-item" data-name={item.title}>
            <span>{itemTitleFor(locale, item)}</span>
            <StatusPill status={item.status} small />
          </button>
        ))}
        {props.findings.map((finding) => (
          <button
            key={finding.id}
            type="button"
            role="option"
            aria-selected={false}
            className="option-row option-row-finding"
            onClick={() => props.onPick({ itemId: null, findingId: finding.id, label: finding.title || t('finding.otherTitle') })}
            disabled={props.busy}
            data-testid="pool-link-finding"
          >
            <span>
              <Icon name="warning" /> {finding.title || t('finding.otherTitle')}
            </span>
          </button>
        ))}
      </div>
    </SheetShell>
  );
}
